<script setup lang="ts">
import type { FAQPage } from 'schema-dts'
import { OWNER_ONBOARDING_2, FLEET_MANAGERS } from '~/constants/pictures'

const { t, locale } = useI18n()
useSeoText({ title: t('pages.rent_my_motorhome.meta.title'), description: t('pages.rent_my_motorhome.meta.description') })

const faqGroup = computed(() => [
  [{
    question: t('pages.rent_my_motorhome.faq.items.vehicle_type.question'),
    answer: t('pages.rent_my_motorhome.faq.items.vehicle_type.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.contract.question'),
    answer: t('pages.rent_my_motorhome.faq.items.contract.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.inspection.question'),
    answer: t('pages.rent_my_motorhome.faq.items.inspection.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.deposit.question'),
    answer: t('pages.rent_my_motorhome.faq.items.deposit.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.damage.question'),
    answer: t('pages.rent_my_motorhome.faq.items.damage.answer'),
  }],
  [{
    question: t('pages.rent_my_motorhome.faq.items.company_registration.question'),
    answer: t('pages.rent_my_motorhome.faq.items.company_registration.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.professional.question'),
    answer: t('pages.rent_my_motorhome.faq.items.professional.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.breakdown.question'),
    answer: t('pages.rent_my_motorhome.faq.items.breakdown.answer'),
  },
  {
    question: t('pages.rent_my_motorhome.faq.items.cancel_remuneration.question'),
    answer: t('pages.rent_my_motorhome.faq.items.cancel_remuneration.answer'),
  }],
])

const faqMicrodata = () => {
  const microdata: Omit<FAQPage, 'mainEntity'> & {
    // mainEntity is always an array of FAQPage['mainEntity']
    mainEntity: (FAQPage['mainEntity'])[]
  } = {
    '@type': 'FAQPage',
    'mainEntity': [],
  }

  faqGroup.value.forEach((faqItem) => {
    faqItem.forEach((faqValue) => {
      microdata.mainEntity.push({
        '@type': 'Question',
        'name': faqValue.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faqValue.answer,
        },
      })
    })
  })

  return microdata
}

useHead({
  script: [
    getJsonLDScript(faqMicrodata()),
  ],
})

const { referential } = storeToRefs(useReferentialStore())
const showGoboonyModal = computed(() =>
  !referential.value?.can_publish_camper,
)
const { goboonyCreateCamperUrl } = useGoboonyRedirectCreateCamper(locale)
</script>

<template>
  <div>
    <AppModalYesboonyCreateCamper
      v-if="showGoboonyModal"
      :country="locale"
      :redirect-url="goboonyCreateCamperUrl"
    />

    <AppRentMyMotorhomeHeader />
    <AppPageSection>
      <div class="flex flex-col lg:flex-row lg:items-center gap-x-4 lg:gap-x-24">
        <i18n-t
          keypath="pages.rent_my_motorhome.rent_with_yescapa.question_dynamic"
          tag="h2"
          scope="global"
          class="text-xl lg:text-2xl font-semibold mt-6 mb-6 lg:my-0 xl:text-4xl"
        >
          <template #emphasis_string>
            <strong class="text-pink-500 lg:inline-block">{{ $t('pages.rent_my_motorhome.rent_with_yescapa.question_emphasis') }}</strong>
          </template>
        </i18n-t>
        <i18n-t
          keypath="pages.rent_my_motorhome.rent_with_yescapa.answer_dynamic"
          tag="p"
          scope="global"
          class="lg:text-xl"
        >
          <template #easier_rental_emphasis_string>
            <strong>{{ $t('pages.rent_my_motorhome.rent_with_yescapa.easier_rental_emphasis_string') }}</strong>
          </template>
          <template #maximizing_income_emphasis_string>
            <strong>{{ $t('pages.rent_my_motorhome.rent_with_yescapa.maximizing_income_emphasis_string') }}</strong>
          </template>
          <template #platform_advantages_emphasis_string>
            <strong>{{ $t('pages.rent_my_motorhome.rent_with_yescapa.platform_advantages_emphasis_string') }}</strong>
          </template>
        </i18n-t>
      </div>
      <AppTableAdvantages class="mt-4" />
    </AppPageSection>
    <AppPageSection class="bg-gray-50">
      <div class="md:flex md:justify-between items-center lg:space-x-24">
        <div class="mb-6 md:mx-4 md:mb-0 md:w-1/3 lg:w-5/12">
          <YscPicture
            :path="OWNER_ONBOARDING_2"
            :sources="[
              { breakpoint: 'xl', width: 500, height: 500 },
              { breakpoint: 'lg', width: 400, height: 400 },
              { breakpoint: 'md', width: 225, height: 225 / 16 * 9 },
            ]"
            with-cover-transformation
            :default-source="{ width: 350, height: 350 / 16 * 9 }"
            alt="-"
            class="h-full w-full rounded"
            fetchpriority="high"
            loading="eager"
          />
        </div>
        <div class="md:mx-4 md:w-2/3 lg:w-1/2">
          <i18n-t
            keypath="pages.rent_my_motorhome.try.title_dynamic"
            tag="h3"
            scope="global"
            class="text-2xl font-semibold leading-tight md:text-4xl mb-6"
          >
            <template #emphasis_string>
              <strong class="text-pink-500 block">{{ $t('pages.rent_my_motorhome.try.title_emphasis') }}</strong>
            </template>
          </i18n-t>
          <div class="flex items-center my-4">
            <div class="mr-4">
              <YscIconsWorkflowCoachingStopwatchHand
                class="h-8 w-8"
                highlight="text-pink-500"
              />
            </div>
            <i18n-t
              keypath="pages.rent_my_motorhome.try.content_fast_dynamic"
              tag="p"
              scope="global"
            >
              <template #announcement_emphasis_string>
                <strong>{{ $t('pages.rent_my_motorhome.try.announcement_emphasis_string') }}</strong>
              </template>
            </i18n-t>
          </div>
          <div class="flex items-center my-4">
            <div class="mr-4">
              <YscIconsTimeMonthly
                class="h-8 w-8"
                highlight="text-pink-500"
              />
            </div>
            <i18n-t
              keypath="pages.rent_my_motorhome.try.content_time_dynamic"
              tag="p"
              scope="global"
            >
              <template #instant_booking_emphasis_string>
                <strong>{{ $t('pages.rent_my_motorhome.try.instant_booking_emphasis_string') }}</strong>
              </template>
            </i18n-t>
          </div>
          <div class="flex items-center my-4">
            <div class="mr-4">
              <YscIconsHappinessEmotions
                class="h-8 w-8"
                highlight="text-pink-500"
              />
            </div>
            <i18n-t
              keypath="pages.rent_my_motorhome.try.content_conditions_dynamic"
              tag="p"
              scope="global"
            >
              <template #terms_emphasis_string>
                <strong>{{ $t('pages.rent_my_motorhome.try.terms_emphasis_string') }}</strong>
              </template>
            </i18n-t>
          </div>
          <div class="w-full flex justify-center lg:justify-start">
            <NuxtLink
              :to="{ name: 'onboarding-step' }"
              class="btn btn-primary mt-4"
            >
              {{ $t('pages.rent_my_motorhome.try.action') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </AppPageSection>
    <AppPageSection>
      <div>
        <i18n-t
          keypath="pages.rent_my_motorhome.faq.title_dynamic"
          tag="h3"
          scope="global"
          class="text-2xl font-semibold leading-tight md:text-4xl mb-6"
        >
          <template #emphasis_string>
            <strong class="text-pink-500 block lg:inline">{{ $t('pages.rent_my_motorhome.faq.title_emphasis') }}</strong>
          </template>
        </i18n-t>

        <div class="grid lg:grid-cols-2 lg:gap-x-32">
          <div
            v-for="(faqItem, key) in faqGroup"
            :key="key"
          >
            <div
              v-for="(faq, faqKey) in faqItem"
              :key="faqKey"
            >
              <YscDetailsAction
                is-summary-center
                class="my-2"
              >
                <template #summary>
                  <h3 class="mr-8 lg:text-xl">
                    {{ faq.question }}
                  </h3>
                </template>
                <template #default>
                  <p class="bg-white text-gray-500 mb-4">
                    {{ faq.answer }}
                  </p>
                </template>
                <template #marker="{ open }">
                  <YscIconsArrowBottom
                    class="h-5 w-5 shrink-0 transition-all"
                    :class="{ 'rotate-180': open }"
                  />
                </template>
              </YscDetailsAction>
              <YscDivider />
            </div>
          </div>
        </div>
      </div>
    </AppPageSection>
    <AppPageSection>
      <div class="lg:flex lg:justify-between items-center lg:space-x-6">
        <div class="mb-6 lg:mb-0 lg:mx-4 lg:w-2/3 xl:w-1/2">
          <i18n-t
            keypath="pages.rent_my_motorhome.more_information.title_dynamic"
            tag="h3"
            scope="global"
            class="text-2xl font-semibold leading-tight md:text-4xl mb-4 lg:mb-6"
          >
            <template #emphasis_string>
              <strong class="text-pink-500 block">{{ $t('pages.rent_my_motorhome.more_information.title_emphasis') }}</strong>
            </template>
          </i18n-t>
          <p class="mb-1 md:mb-2">
            {{ $t('pages.rent_my_motorhome.more_information.pre_content') }}
          </p>
          <i18n-t
            keypath="pages.rent_my_motorhome.more_information.content_dynamic"
            tag="p"
            scope="global"
          >
            <template #emphasis_string>
              <AppHighlightContent>{{ $t('pages.rent_my_motorhome.more_information.content_emphasis') }}</AppHighlightContent>
            </template>
          </i18n-t>
        </div>
        <div class="flex flex-col items-center mb-6 lg:mx-4 lg:mb-0 lg:w-1/3 xl:w-1/2">
          <YscPicture
            :path="FLEET_MANAGERS"
            :sources="[
              { breakpoint: 'xl', width: 425, height: 425 / 16 * 9 },
              { breakpoint: 'lg', width: 290, height: 290 / 16 * 9 },
              { breakpoint: 'md', width: 600, height: 600 / 16 * 9 },
            ]"
            alt="-"
            :default-source="{ width: 350, height: 350 / 16 * 9 }"
            class="w-2/3"
            fetchpriority="high"
            loading="eager"
          />
          <YscCard class="lg:shadow bg-gray-50">
            <div class="flex flex-col-reverse items-center lg:flex-row">
              <NuxtLink
                :to="{ name: 'earnings' }"
                class="btn btn-primary text-center rounded mr-3 w-full lg:w-auto"
              >
                {{ $t('components.app_rent_my_motorhome.header.simulation_action') }}
              </NuxtLink>
              <p class="text-gray-500 text-sm text-center px-6 lg:px-0 mb-3 lg:mb-0 lg:text-left">
                {{ $t('pages.rent_my_motorhome.more_information.content_cta') }}
              </p>
            </div>
          </YscCard>
        </div>
      </div>
    </AppPageSection>
    <!-- FIXME: Enable when we have a link to the webinar Yescapa -->
    <!-- <AppPageSection is-sub-section>
      <div class="flex flex-col md:flex-row items-center justify-between bg-pink-50 rounded px-6 py-4">
        <div class="flex items-center w-full md:w-1/2 mb-3 md:mb-0">
          <YscIconsMeetingTeamMonitor
            highlight="text-pink-500"
            class="w-24 h-24 md:h-16 md:w-16 mr-4"
          />
          <p class="text-sm lg:text-base">
            Vous pouvez également vous inscrire à notre webinar gratuit durant lequel vous pourrez poser toutes vos questions en direct
          </p>
        </div>
        <NuxtLink class="btn bg-white text-pink-500 text-sm lg:text-base">
          Je m’inscris au prochain webinar Yescapa
        </NuxtLink>
      </div>
    </AppPageSection> -->
  </div>
</template>
